import React, { useEffect } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  buildNotificationUrl, formatEventType, eventTypeColor,
  siteOf, participantOf, studyOf,
} from './notificationNavigation';

/**
 * Full detail view for a single notification. Opened from both the
 * notifications table and the bell dropdown so the (often truncated)
 * title/body can be read in full, with an optional deep link into the app.
 */
const NotificationDetailModal = ({ notification, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!notification) return null;

  const color = eventTypeColor(notification.event_type_key);
  const url = buildNotificationUrl(notification);
  const received = new Date(notification.created_at).toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  });

  const meta = [
    { label: 'Site', value: siteOf(notification) },
    { label: 'Participant', value: participantOf(notification) },
    { label: 'Study', value: studyOf(notification) },
    { label: 'Received', value: received },
    { label: 'From', value: notification.actor_name || 'System' },
  ].filter((m) => m.value);

  const handleGo = () => {
    onClose();
    navigate(url);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-[60] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Notification details"
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${color.badge}`}>
              {formatEventType(notification.event_type_key)}
            </span>
            <h3 className="text-lg font-semibold text-gray-900 mt-2 break-words">
              {notification.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-5 max-h-[50vh] overflow-y-auto">
          {notification.body && (
            <p className="text-sm text-gray-600 whitespace-pre-line break-words">
              {notification.body}
            </p>
          )}
          {meta.length > 0 && (
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm border-t border-gray-100 pt-4">
              {meta.map((m) => (
                <div key={m.label}>
                  <dt className="text-[11px] uppercase tracking-wide text-gray-400">{m.label}</dt>
                  <dd className="text-gray-700 mt-0.5">{m.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
          {url && (
            <button
              onClick={handleGo}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Go to related page <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationDetailModal;
